import { WSFilter, WSFilterOption } from "@wingspanhq/fe-component-library";
import { useMemo } from "react";
import { queriesPayeeRows, queriesPayPeriods } from "@query";
import { selectorPayPeriodOptions } from "../../../selectors/selectorPayPeriodOptions";
import { selectorWorkLogEngagementOptions } from "../../../selectors/selectorWorkLogEngagementOptions";

export function useBuildConfig() {
  // The only engagement identifier the v3 `listWorkLogs` filter accepts is
  // `payeeEngagementId` (the payer↔payee relationship), so the "Engagement"
  // filter lists the payer's payee-engagements keyed on it. Sourced from the v3
  // payee-row search — each row can hold several engagements.
  const queryPayees = queriesPayeeRows.searchPayeeRows.useQueryAll(
    { filter: { engagements_isWorkLoggingEnabled: { eq: true } } },
    { refetchOnMount: false, retry: false }
  );

  const engagementOptions = useMemo(
    (): WSFilterOption<"checkbox">[] =>
      // Every work-logging engagement, live or not: a terminated assignment can
      // still have work logs worth filtering to (the create modal narrows this).
      selectorWorkLogEngagementOptions(queryPayees.flatData || []).map(
        option => ({ name: option.value, label: option.label })
      ),
    [queryPayees.flatData]
  );

  // Pay period is the design's headline work-log grouping ("filters and tables —
  // pay period the work log is connected to"), and `listWorkLogs` filters on it
  // directly.
  const queryPayPeriods = queriesPayPeriods.listPayPeriods.useQueryAll(
    undefined,
    { refetchOnMount: false, retry: false }
  );

  const payPeriodOptions = useMemo(
    (): WSFilterOption<"checkbox">[] =>
      selectorPayPeriodOptions(queryPayPeriods.flatData || []).map(option => ({
        name: option.value,
        label: option.label
      })),
    [queryPayPeriods.flatData]
  );

  return useMemo(
    (): WSFilter[] => [
      {
        title: "Pay period",
        name: "payPeriodId",
        type: "select",
        options: payPeriodOptions,
        searchable: true,
        loading: queryPayPeriods.isLoading
      },
      {
        title: "Engagement",
        name: "payeeEngagementId",
        type: "select",
        options: engagementOptions,
        searchable: true,
        loading: queryPayees.isLoading
      }
    ],
    [
      engagementOptions,
      payPeriodOptions,
      queryPayees.isLoading,
      queryPayPeriods.isLoading
    ]
  );
}
